import { useState, useEffect, useContext } from "react"
import { Upload, FileText, X, AlertCircle, CheckCircle2 } from "lucide-react"

import { AuthContext } from "../../../utils/auth.js"
import validateFiles from "../../../components/utils/validateFiles.js"
import SkippedFilesNotice from "../../../components/utils/SkippedFilesNotice.js"
import axios from "axios";
import toast from 'react-hot-toast';

import { pdfjs } from 'react-pdf';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.js`;

const mimeTypesMap = [
    // pdf
    'application/pdf',
    // images
    'image/png',
    'image/jpeg',
    'image/jpeg',
    'image/tiff',
    // word
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    // excel
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    // csv
    'text/csv',
    // text
    'text/plain',
];

export function UploadFilesModal({ open, onOpenChange, currentBatchId, classificationId }) {
    const [files, setFiles] = useState([])
    const [skippedFiles, setSkippedFiles] = useState([])
    const [isCheckingFiles, setIsCheckingFiles] = useState(false)
    const [isDragging, setIsDragging] = useState(false)
    const [isUploading, setIsUploading] = useState(false)
    const [uploadProgress, setUploadProgress] = useState(0)
    const [uploadComplete, setUploadComplete] = useState(false)
    const [isError, setIsError] = useState(false)
    const [creditsError, setCreditsError] = useState(false)
    const [errorMessage, setErrorMessage] = useState("")
    const [showCreditsConfirmation, setShowCreditsConfirmation] = useState(false)

    const serverUrl = process.env.REACT_APP_SERVER_URL;
    const { currentUser } = useContext(AuthContext);
    const [dataToRedirect, setDataToRedirect] = useState(null)
    const [noOfPages, setNoOfPages] = useState(0);


    const handleDragOver = (e) => {
        e.preventDefault()
        setIsDragging(true)
    }

    const handleDragLeave = () => {
        setIsDragging(false)
    }

    const addFiles = async (newFiles) => {
        setIsCheckingFiles(true)

        const { validFiles, skippedFiles: newSkippedFiles } = await validateFiles(newFiles)

        setFiles((currentFiles) => [...currentFiles, ...validFiles])
        setSkippedFiles((currentSkippedFiles) => [...currentSkippedFiles, ...newSkippedFiles])
        setIsCheckingFiles(false)

        if (newSkippedFiles.length > 0) {
            toast.error(`${newSkippedFiles.length} file(s) could not be read and will be skipped.`)
        }
    }

    const handleDrop = (e) => {
        e.preventDefault()
        setIsDragging(false)

        if (e.dataTransfer.files) {
            const droppedFiles = Array.from(e.dataTransfer.files)
            const acceptedFiles = droppedFiles.filter(file => mimeTypesMap.includes(file.type))

            // Show toast if any files were rejected
            if (acceptedFiles.length < droppedFiles.length) {
                const rejectedCount = droppedFiles.length - acceptedFiles.length
                toast.error(`${rejectedCount} file(s) were rejected. Only PDF, image, Word, Excel, CSV and text files are supported.`)
            }

            addFiles(acceptedFiles)
        }
    }

    const handleFileChange = (e) => {
        if (e.target.files) {
            const newFiles = Array.from(e.target.files)
            e.target.value = ""
            addFiles(newFiles)
        }
    }

    const removeFile = (index) => {
        const newFiles = [...files]
        newFiles.splice(index, 1)
        setFiles(newFiles)
    }

    const handleUpload = () => {
        if (files.length === 0) return

        setIsUploading(true)
        setUploadProgress(0)

        if (currentBatchId) {
            const formData = new FormData();
            formData.append("classificationId", classificationId);
            formData.append("batchId", currentBatchId);
            files.forEach((file) => {
                formData.append("files", file);
            })

            console.log(formData);

            currentUser.getIdToken().then((idToken) => {
                const url = serverUrl + "/documentClassification/uploadFiles";
                axios.post(url, formData, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        "Access-Control-Allow-Origin": "*",
                        Authorization: `Bearer ${idToken}`
                    },
                    onUploadProgress: (progressEvent) => {
                        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        setUploadProgress(percentCompleted);
                    }
                }).then((response) => {
                    if (response.status === 200) {
                        //console.log(response.data);
                        setIsUploading(false);
                        setUploadComplete(true);
                        setUploadProgress(100);
                        setDataToRedirect(response.data);
                    }
                }).catch((error) => {
                    console.log(error);
                    if (error.response.data === "not_enough_credits") {
                        setErrorMessage("You don't have enough credits to perform this extraction.\nPlease buy more credits to continue.");
                    }
                    setIsError(true);
                });
            });
            return;
        }

        if (classificationId) {
            const formData = new FormData();
            formData.append("classificationId", classificationId);
            files.forEach((file) => {
                formData.append("files", file);
            })

            console.log(formData);

            currentUser.getIdToken().then((idToken) => {
                const url = serverUrl + "/documentClassification/uploadFiles";
                axios.post(url, formData, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        "Access-Control-Allow-Origin": "*",
                        Authorization: `Bearer ${idToken}`
                    },
                    onUploadProgress: (progressEvent) => {
                        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        setUploadProgress(percentCompleted);
                    }
                }).then((response) => {
                    if (response.status === 200) {
                        //console.log(response.data);
                        setIsUploading(false);
                        setUploadComplete(true);
                        setUploadProgress(100);
                        setDataToRedirect(response.data);
                    }
                }).catch((error) => {
                    //error.response.data.message = "Not enough credits"
                    if (error.response.data.message === "Not enough credits") {
                        setErrorMessage("You don't have enough credits to perform this extraction.\nPlease buy more credits to continue.");
                        setCreditsError(true);

                    } else {
                        setErrorMessage("An error occurred while uploading the files.\nPlease try again.");
                    }
                    setIsError(true);
                });
            });
        }
        // Simulate upload progress
        // const interval = setInterval(() => {
        //     setUploadProgress((prev) => {
        //         if (prev >= 100) {
        //             clearInterval(interval)
        //             setIsUploading(false)
        //             setUploadComplete(true)
        //             return 100
        //         }
        //         return prev + 1
        //     })
        // }, 30)
    }


    const resetModal = () => {
        setFiles([])
        setSkippedFiles([])
        setIsCheckingFiles(false)
        setIsUploading(false)
        setUploadProgress(0)
        setUploadComplete(false)
        setCreditsError(false)
        setErrorMessage("")
        setIsError(false)
    }

    const handleClose = () => {
        //redirect to another page
        if (dataToRedirect) {
            window.location.href = `/classifications/${dataToRedirect.classificationId}/batch/${dataToRedirect.batchId}`
        }
        resetModal()
        onOpenChange(false)
    }

    const handleCloseX = () => {
        resetModal()
        onOpenChange(false)
    }

    const handleTryAgain = () => {
        setIsError(false)
        setErrorMessage("")
        setUploadComplete(false)
        setUploadProgress(0)
        setIsUploading(false)
    }

    const handleBuyCredits = () => {
        window.location.href = "/billing"
    }


    const WORDS_PER_PAGE = 500;

    const getNoOfPagesFromTxt = async (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = (event) => {
                const content = event.target.result;
                const words = content.trim().split(/\s+/);
                const numberOfWords = words.length;
                const numberOfPages = Math.ceil(numberOfWords / WORDS_PER_PAGE);
                resolve(numberOfPages);
            };

            reader.onerror = (error) => {
                reject(error);
            };

            reader.readAsText(file);
        });
    };

    const getNoOfPagesFromPDF = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsArrayBuffer(file);
            reader.onload = async () => {
                try {
                    const arrayBuffer = reader.result;
                    const pdf = await pdfjs.getDocument(arrayBuffer).promise;
                    resolve(pdf.numPages);
                } catch (error) {
                    reject(error);
                }
            };
            reader.onerror = error => reject(error);
        });
    };

    const getNoOfPagesFromWord = async (file) => {
        return 1;
    }

    useEffect(() => {
        const fetchNumberOfPages = async () => {
            if (files) {
                let pages = 0;
                for (const file of files) {
                    try {
                        if (file.type === "application/pdf") {
                            pages += await getNoOfPagesFromPDF(file);;
                        } else if (file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" || file.type === "application/msword") {
                            pages += await getNoOfPagesFromWord(file);
                        } else if (file.type === "text/plain") {
                            pages += await getNoOfPagesFromTxt(file);
                        } else {
                            pages += 1;
                        }
                    } catch (error) {
                        pages += 1;
                    }
                }

                setNoOfPages(pages);
            }
        }

        if (files) {
            fetchNumberOfPages();
        }

        return () => { }
    }, [files]);

    return (
        open && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                <div className="bg-white rounded-lg shadow-lg w-full max-w-[700px] max-h-[90vh] overflow-auto relative">
                    {/*Close Button*/}
                    <button
                        onClick={() => {
                            handleCloseX()
                        }}
                        className="absolute top-6 right-6 text-gray-400 hover:text-gray-600"
                    >
                        <X className="h-5 w-5" />
                    </button>

                    {/* Header */}
                    <div className="p-6">
                        <h3 className="text-base font-semibold leading-6 text-gray-900">Upload Documents to {!currentBatchId ? "a new batch" : "batch"}</h3>
                        <p className="text-sm text-gray-500 mt-1 max-w-xl">
                            Upload your documents here. The system will automatically classify them based on their content and document type. You can upload multiple files at once.
                        </p>
                    </div>

                    {/*upload error view*/}
                    {isError ? (
                        <>
                            <div className="flex flex-col items-center justify-center py-8 text-center">
                                <AlertCircle className="h-10 w-10 text-red-500 mb-2" />
                                <h2 className="text-xl font-semibold mb-2">Upload Failed!</h2>
                                <p className="text-gray-500 mb-6">
                                    {errorMessage}
                                </p>
                            </div>
                            {/* Footer */}
                            <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                {creditsError ? (
                                    <button
                                        type="button"
                                        onClick={() => handleBuyCredits()}
                                        className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:ml-3 sm:w-auto"
                                    >
                                        Buy Credits
                                    </button>
                                ) : (

                                    <button
                                        type="button"
                                        onClick={() => handleTryAgain()}
                                        className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:ml-3 sm:w-auto"
                                    >
                                        Try Again
                                    </button>
                                )}
                                <button
                                    onClick={() => handleCloseX()}
                                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                >
                                    Close
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            {/* Upload Complete View */}
                            {uploadComplete ? (
                                <>
                                    <div className="flex flex-col items-center justify-center py-8 text-center">
                                        <CheckCircle2 className="h-10 w-10 text-green-500 mb-2" />
                                        <h2 className="text-xl font-semibold mb-2">Upload Complete!</h2>
                                        <p className="text-gray-500 mb-6">
                                            Your documents have been uploaded and are being processed for classification.
                                            {skippedFiles.length > 0 && ` ${skippedFiles.length} file(s) were skipped because they could not be read.`}
                                        </p>
                                    </div>
                                    {/* Footer */}
                                    <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                        {currentBatchId ? (
                                            <button
                                                type="button"
                                                onClick={() => handleCloseX()}
                                                className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:ml-3 sm:w-auto"
                                            >
                                                Close
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => handleClose()}
                                                className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:ml-3 sm:w-auto"
                                            >
                                                View Batch Results
                                            </button>
                                        )}
                                        <button
                                            onClick={() => resetModal()}
                                            className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                        >
                                            Upload More Files
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <div className="grid gap-6 p-6">
                                    {/* Dropzone */}
                                    <div
                                        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer ${isDragging ? "border-indigo-600 bg-indigo-50" : "border-gray-300"
                                            }`}
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDrop}
                                    >
                                        <div className="flex flex-col items-center justify-center">
                                            <Upload className="h-12 w-12 text-gray-400 mb-4" />
                                            <h3 className="text-lg font-medium mb-1">Drag files here or click to browse</h3>
                                            <p className="text-sm text-gray-500 mb-4">Support for Photos, PDFs, Word and .txt files</p>
                                            <button
                                                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100"
                                                onClick={() => document.getElementById("file-upload-modal")?.click()}
                                            >
                                                Browse Files
                                            </button>
                                            <input
                                                id="file-upload-modal"
                                                type="file"
                                                multiple
                                                accept=".pdf,.png,.jpg,.jpeg,.tiff,.doc,.docx,.xls,.xlsx,.csv,.txt,application/pdf,image/png,image/jpeg,image/tiff,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,text/plain"
                                                className="hidden"
                                                onChange={handleFileChange}
                                            />
                                        </div>
                                    </div>

                                    {/* Skipped Files Notice */}
                                    <SkippedFilesNotice skippedFiles={skippedFiles} onDismiss={() => setSkippedFiles([])} />

                                    {isCheckingFiles && (
                                        <p className="text-sm text-gray-500">Checking files...</p>
                                    )}

                                    {/* Selected Files List */}
                                    {files.length > 0 && (
                                        <div className="border rounded-md p-4">
                                            <h3 className="font-medium mb-3">Selected Files ({files.length})</h3>
                                            <div className="space-y-2 max-h-[200px] overflow-auto">
                                                {files.map((file, index) => (
                                                    <div key={index} className="flex items-center justify-between p-2 border rounded-md">
                                                        <div className="flex items-center">
                                                            <FileText className="h-5 w-5 text-gray-400 mr-2" />
                                                            <div>
                                                                <p className="font-medium">{file.name}</p>
                                                                <p className="text-sm text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                                                            </div>
                                                        </div>
                                                        <button
                                                            onClick={() => removeFile(index)}
                                                            disabled={isUploading}
                                                            className="p-1 text-gray-500 hover:text-red-600"
                                                        >
                                                            <X className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>

                                            {isUploading && (
                                                <div className="mt-4">
                                                    <div className="flex justify-between text-sm mb-1">
                                                        <span>Uploading...</span>
                                                        <span>{uploadProgress}%</span>
                                                    </div>
                                                    <div className="w-full bg-gray-200 rounded h-2">
                                                        <div
                                                            className="bg-indigo-600 h-2 rounded"
                                                            style={{ width: `${uploadProgress}%` }}
                                                        />
                                                    </div>
                                                </div>
                                            )}

                                            <div className="mt-4 flex justify-between items-center">
                                                <div className="text-sm text-gray-500">
                                                    <span className="font-medium">{noOfPages} estimated pages</span>
                                                </div>
                                                <button
                                                    onClick={() => {
                                                        setShowCreditsConfirmation(true)
                                                    }}
                                                    disabled={files.length === 0 || isUploading || isCheckingFiles}
                                                    className={`mt-3 ml-2 inline-flex items-center gap-x-1.5 rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm  focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 ${isUploading || isCheckingFiles || files.length === 0
                                                        ? "bg-gray-400 cursor-not-allowed"
                                                        : "bg-indigo-600  hover:bg-indigo-500"
                                                        }`}
                                                >
                                                    {isUploading ? "Uploading..." : "Continue"}
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Alert Box */}
                                    <div className="flex gap-2 bg-yellow-50 border border-yellow-200 p-4 rounded-md">
                                        <AlertCircle className="h-5 w-5 text-yellow-500 flex-shrink-0" />
                                        <div>
                                            <h4 className="font-semibold text-sm mb-1">Important</h4>
                                            <p className="text-sm text-gray-700">
                                                Documents will be automatically classified based on the document types defined previously. If a
                                                document matches a document type with a linked extraction template, data extraction will be performed
                                                automatically.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                    {/* Credits confirmation dialog */}
                    {showCreditsConfirmation && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                            <div className="bg-white rounded-lg shadow-lg w-full max-w-[600px] mx-4">
                                {/* Header */}
                                <div className="p-6 pb-0">
                                    <h3 className="text-base font-semibold leading-6 text-gray-900">
                                        Confirm Credit Usage
                                    </h3>
                                    <p className="mt-1 max-w-xl text-sm text-gray-500">
                                        Processing these {files.length} files will use approximately <strong>{noOfPages} credits</strong> from your
                                        account for classification.
                                    </p>
                                </div>
                                {/* Body */}
                                <div className="p-6 space-y-4">
                                    {/* Alert Box */}
                                    <div className="flex gap-2 bg-yellow-50 border border-yellow-200 p-4 rounded-md">
                                        <AlertCircle className="h-5 w-5 text-yellow-500 flex-shrink-0" />
                                        <div>
                                            <h4 className="font-semibold text-sm mb-1">Additional Extraction Charges</h4>
                                            <p className="text-sm text-gray-700">
                                                If any documents match a document type with a linked extraction template, additional credits will be
                                                charged for the extraction process.
                                            </p>
                                        </div>
                                    </div>

                                </div>

                                {/* Document Type Form Footer */}
                                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 rounded-lg">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowCreditsConfirmation(false)
                                            handleUpload()
                                        }}
                                        className={'inline-flex w-full justify-center rounded-md  px-3 py-2 text-sm font-semibold text-white shadow-sm  sm:ml-3 sm:w-auto bg-indigo-600 hover:bg-indigo-500'}
                                    >
                                        Upload
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowCreditsConfirmation(false)
                                        }}
                                        className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        )
    )
}
